<template>
  <PageContainer
    class="supplier-info-container"
    v-if="factory == null || factory.status !== 0"
  >
    <div
      class="left"
      v-if="factory"
    >
      <div class="supplier-avatar">
        <Image
          class="supplier-avatar__image"
          :src="factory.companyLogo ?? DefaultImage"
        />
        <img
          v-if="factory.honestMerchant >= 5"
          class="best-icon"
          src="@/assets/illus/illus-suppier-avatar-best.svg"
          alt=""
        >
      </div>
      <Popover
        class="supplier-info"
        arrow-color="#E0EBFF"
        placement="bottom"
        @show="handlePopoverShow"
      >
        <template #reference="{ visible }">
          <div class="title">
            <div class="title-l">
              {{ factory.supplierName }}
              <Tooltip content="There are user reports, please choose carefully.">
                <Icon
                  v-if="factory.currentUserReport"
                  style="color: #DB7E25;"
                  name="icon-warning-outline"
                />
              </Tooltip>
            </div>
            <Icon
              :class="{ rotate: visible }"
              class="icon"
              name="arrow-down-l-2"
            />
          </div>
          <div
            class="rating-info"
          >
            <img
              src="@/assets/illus/illus-supplier-trusted.svg"
              alt=""
            >
            <div class="rating">
              <div class="text">
                Credit Rating
              </div>
              <div
                class="star"
                v-if="factory.supplierRatingVo"
              >
                <StarRate
                  :value="factory.supplierRatingVo.compositeService / 5"
                  :count="1"
                />
                {{ factory.supplierRatingVo.compositeService }}
                <span class="suffix">&nbsp;/&nbsp;5</span>
              </div>
              <div
                class="star"
                v-else
              >
                -
              </div>
            </div>
          </div>
          <div class="supplier-tags">
            <div
              class="tag"
              v-for="(tag, index) of factory.tagNameList.slice(0, 2)"
              :key="index"
            >
              {{ tag }}
            </div>
          </div>
        </template>
        <RatingDlg
          @report="handleReport"
          :factory="factory"
        />
      </Popover>
      <Button
        class="follow-factory"
        :class="{
          'follow-factory--followed': isFollowed
        }"
        @click="onFollowClick(factory.sellerOpenId)"
        :loading="following"
      >
        <template v-if="isFollowed">
          <Icon
            class="follow-factory__icon"
            name="right-l"
          />
          Followed
        </template>
        <template v-else>
          Follow Factory
        </template>
      </Button>
    </div>
    <FactorySkeleton
      class="left"
      v-else
    />
    <FactoryProducts
      v-if="showTop"
      :factory-id="factoryId"
    />
    <SearchInput
      class="search-wrapper"
      placeholder="Search in factory"
      v-model="searchText"
      @clear="onSearch"
      @search="onSearch"
    />

    <ReportDialog
      v-if="reportDialogVisible"
      :target-id="factoryId"
      @close="reportDialogVisible = false"
      type="SUPPLIER"
    />
  </PageContainer>
</template>

<script setup>
import { ref } from 'vue'
import DefaultImage from '@/assets/factory/default-logo.svg'
import Icon from '@/components/Icon/Icon.vue'
import Popover from '@/components/Popover/index.vue'
import RatingDlg from './RatingDlg.vue'
import { useFactory } from './useFactory'
import { Image } from '../Image'
import { StarRate } from '../StarRate'
import { Button } from '../Button'
import FactorySkeleton from './FactorySkeleton.vue'
import FactoryProducts from './FactoryProducts.vue'
import { PageContainer } from '../PageLayout'
import { SearchInput } from '../SearchInput'
import { ReportDialog } from '../ReportDialog'
import { Tooltip } from '../Tooltip'
import { buriedPointUtil } from '@/utils/utils'

const props = defineProps({
  factoryId: {
    type: String,
    default: null
  },
  isLoadProduct: {
    type: Boolean,
    default: false
  },
  defaultInput: {
    type: String,
    default: ''
  },
  showTop: {
    type: Boolean,
    default: true
  }
})
const factoryId = computed(() => props.factoryId)

const emit = defineEmits(['search'])

const searchText = ref(props.defaultInput)

const { factory, isFollowed, toggleFollow } = useFactory(factoryId)

const following = ref(false)

const reportDialogVisible = ref(false)

const onFollowClick = async () => {
  following.value = true
  await toggleFollow('Top')
  following.value = false
}

const onSearch = () => {
  emit('search', {
    text: searchText.value,
    supplierName: factory.value?.supplierName
  })
  if (searchText.value) {
    buriedPointUtil({ eventId: 'PPD210', others: { keyword: searchText.value } })
  }
}

const handleReport = () => {
  reportDialogVisible.value = true
  buriedPointUtil({ eventId: 'PPD206', others: { type: 'Factory', objectId: factoryId.value } })
}

const handlePopoverShow = () => {
  buriedPointUtil({ eventId: 'PPD205' })
}
</script>

<style lang='less'>
.supplier-info-container {
  padding: 12px 0;
  background-color: #F4F7FF;

  .page-container__content {
    display: flex;
    align-items: center;
    gap: 26px;
    height: 64px;
  }

  .left {
    margin-right: auto;
    display: flex;

    @media (min-width: 1024px) {
      max-width: 50%;
    }

    .supplier-avatar {
      flex-shrink: 0;
      position: relative;
      width: 64px;
      height: 64px;

      &__image {
        width: 100%;
        height: 100%;
        border-radius: 50%;
      }

      .best-icon {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 20px;
        height: 20px;
      }
    }
    .supplier-info {
      flex: 1;
      min-width: 0;
      margin-left: 16px;
      cursor: pointer;

      .title {
        display: flex;
        align-items: center;
        gap: 12px;
        .title-l {
          font-family: Young Serif;
          font-size: 14px;
          line-height: 18px;
          color: #232323;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .icon {
          transition: transform .3s ease-in;
          &.rotate {
            transform: rotate(180deg);
          }
        }
      }
      .rating-info {
        margin-top: 6px;
        display: flex;
        align-items: center;
        gap: 6px;
        .rating {
          display: flex;
          align-items: center;
          gap: 2px;
          color: #232323;
          font-size: 11px;
          font-family: mon-500;
          .star {
            display: flex;
            align-items: center;
            margin-left: 2px;
            .suffix {
              font-size: 10px;
              color: #6E717A;
            }
          }
        }
      }
      .supplier-tags {
        display: flex;
        gap: 4px;
        margin-top: 4px;
        .tag {
          padding: 2px 4px;
          border: 1px solid #DFDFDF;
          border-radius: 2px;
          color: #232323;
          font-size: 10px;
          font-family: mon-600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
    .follow-factory {
      flex-shrink: 0;
      margin-left: 24px;
      width: 112px;
      height: 28px;
      font-family: mon-600;
      font-size: 12px;
      line-height: 16px;
      background-color: #232323;
      color: #FFFFFF;
      border-radius: 4px;
      border: none;
      gap: 2px;

      &__icon {
        font-size: 16px;
      }

      &--followed {
        background-color: #F5F5F5;
        color: #232323;
      }
    }
  }
  .search-wrapper {
    width: 24%;
    min-width: 288px;
    max-width: 432PX;
  }
}
</style>
